/**
 * P1-B SCAFFOLD — In-WhatsApp Angehörigenpflege wage-calculator quiz flow.
 *
 * STATUS: This is a DESIGN-LEVEL config that describes the quiz step sequence
 * and disqualifier branches. It is NOT wired into the live agent flow yet.
 * It does not send messages, does not call ChatSuite, and does not capture
 * leads on its own. The live wiring (rendering each step as a ChatSuite
 * button/list message, threading answers into `submit_lead_data`, and invoking
 * `estimateCaregiverWage` at the reveal step) is the remaining P1-B work and
 * depends on Spitex Frei intake criteria (I3) being confirmed.
 *
 * What IS real and usable today:
 * - `estimateCaregiverWage` (see `./wage-estimate`) — the pure, tested estimate
 *   engine the reveal step will call.
 * - This config object, which the agent-flow builder can consume to render the
 *   quiz once it is wired. It mirrors the leaders' proven sequence:
 *     situation → relationship → billable tasks → daily hours → canton →
 *     reveal estimate → consent + human handoff.
 *
 * SCOPE: Selenos is the lead-gen VENDOR. The disqualifier criteria, served
 * cantons, and acceptance gates marked PLACEHOLDER below belong to Spitex Frei
 * (input I3) and must be confirmed before production. We never invent them.
 *
 * GOAL: capture the lead + book a human follow-up — NOT close in chat. The
 * estimate is an anchor that earns the contact details; a Spitex Frei
 * coordinator does the real qualification and offer.
 */

import {
  BILLABLE_TASK_LABELS_DE,
  PLACEHOLDER_BILLABLE_TASKS,
  PLACEHOLDER_SUPPORTED_CANTONS,
  type WageEstimateIneligibilityReason,
} from './wage-estimate';

/**
 * The interactive-render kind for a quiz step. Maps to the ChatSuite send tool
 * the live wiring will use:
 * - `buttons` → `chatsuite_send_buttons` (≤3 quick-reply options)
 * - `list` → `chatsuite_send_list` (>3 options, e.g. canton picker, task multi-select)
 * - `text` → plain `chatsuite_send_message` (free-text answer, e.g. relationship)
 */
export type QuizStepRender = 'buttons' | 'list' | 'text';

/** One option in a buttons/list step. `value` is the stable key stored on the lead. */
export interface QuizStepOption {
  value: string;
  labelDe: string;
}

/**
 * A single quiz step. `leadField` is the `submit_lead_data` field path the
 * answer maps onto (so the quiz reuses the existing incremental capture path
 * rather than inventing a parallel store). `null` for the reveal/handoff steps
 * that capture nothing new.
 */
export interface WageQuizStep {
  /** Stable step id, in ask order. */
  id: string;
  /** Render kind for the live wiring. */
  render: QuizStepRender;
  /** German prompt shown to the user. */
  promptDe: string;
  /** Options for buttons/list steps; omitted for `text` and synthetic steps. */
  options?: QuizStepOption[];
  /** Whether the user may pick more than one option (e.g. the tasks step). */
  multiSelect?: boolean;
  /**
   * The `submit_lead_data` field this answer maps to, or null for synthetic
   * steps (reveal, handoff) that store nothing new.
   */
  leadField: string | null;
  /** Human note for the wiring author; never shown to the user. */
  note?: string;
}

/**
 * A disqualifier branch: when `when` is true, the agent shows `messageDe`,
 * does NOT proceed to the wage reveal, and (per always-save-leads) still saves
 * the lead but as a non-qualifying status — it is never delivered as an
 * accepted Angehörigenpflege lead to Spitex Frei.
 *
 * `reason` ties a branch to the matching `estimateCaregiverWage` ineligibility
 * reason where one exists, so the live wiring can drive the branch directly off
 * the estimate result instead of re-deriving the condition.
 */
export interface WageQuizDisqualifier {
  id: string;
  /** Plain-language trigger condition (the wiring implements the check). */
  when: string;
  /** German message shown when this branch fires. */
  messageDe: string;
  /** Estimate ineligibility reason this branch corresponds to, if any. */
  reason?: WageEstimateIneligibilityReason;
}

export interface WageQuizFlow {
  /** Ordered quiz steps. */
  steps: WageQuizStep[];
  /** Disqualifier branches checked as answers come in. */
  disqualifiers: WageQuizDisqualifier[];
  /**
   * Reveal-step message template (German). `{{annual}}` / `{{monthly}}` are
   * filled from `estimateCaregiverWage`; `{{disclaimer}}` MUST be included so
   * the figure always reads as an estimate, never a guarantee (WIP P1-C).
   */
  revealTemplateDe: string;
  /** Handoff message after the reveal, asking consent + booking follow-up. */
  handoffTemplateDe: string;
}

/** Task options for the multi-select step, derived from the billable-task config. */
const taskOptions: QuizStepOption[] = PLACEHOLDER_BILLABLE_TASKS.map(
  (value) => ({
    value,
    labelDe: BILLABLE_TASK_LABELS_DE[value] ?? value,
  }),
);

/** Canton options for the picker, derived from the supported-canton config. */
const cantonOptions: QuizStepOption[] = PLACEHOLDER_SUPPORTED_CANTONS.map(
  (value) => ({ value, labelDe: value }),
);

/**
 * The Angehörigenpflege in-chat wage-quiz flow.
 *
 * PLACEHOLDER content (German copy, options, gates) tracks the supply
 * conversation config + the wage-estimate placeholders; all of it is subject to
 * Spitex Frei's confirmed intake criteria (I3) before going live.
 */
export const spitexAngehoerigenpflegeWageQuizFlow: WageQuizFlow = {
  steps: [
    {
      id: 'situation',
      render: 'buttons',
      promptDe: 'Pflegen Sie aktuell einen Angehörigen zu Hause?',
      options: [
        { value: 'yes', labelDe: 'Ja, ich pflege jemanden' },
        { value: 'planning', labelDe: 'Ich plane es' },
        { value: 'no', labelDe: 'Nein' },
      ],
      // Routes onto the existing supply routing field.
      leadField: 'customFields.serviceInterest',
      note: 'Map "yes"/"planning" → angehoerigenpflege; "no" → disqualifier "no_caregiving".',
    },
    {
      id: 'relationship',
      render: 'text',
      promptDe: 'Wen pflegen Sie? (z.B. Mutter, Vater, Partner/in)',
      leadField: 'customFields.careRelationship',
    },
    {
      id: 'tasks',
      render: 'list',
      promptDe:
        'Welche dieser Tätigkeiten übernehmen Sie? (Mehrfachauswahl möglich)',
      options: taskOptions,
      multiSelect: true,
      // Stored as free-text careActivities on the lead AND passed to
      // estimateCaregiverWage as the `tasks` array at the reveal step.
      leadField: 'customFields.careActivities',
      note: 'Selected option values feed estimateCaregiverWage({ tasks }). Zero billable selections → disqualifier "no_billable_tasks".',
    },
    {
      id: 'daily_hours',
      render: 'buttons',
      promptDe: 'Wie viele Stunden pro Tag pflegen Sie ungefähr?',
      options: [
        { value: '1', labelDe: 'Ca. 1 Std.' },
        { value: '3', labelDe: 'Ca. 3 Std.' },
        { value: '6', labelDe: '6 Std. oder mehr' },
      ],
      // Stored as careHoursPerWeek (hours/day * 7) on the lead; passed to
      // estimateCaregiverWage as hoursPerDay at the reveal step.
      leadField: 'customFields.careHoursPerWeek',
      note: 'Button value is hours/DAY for the estimate; persist careHoursPerWeek = hoursPerDay * 7 to match the existing scoring rules.',
    },
    {
      id: 'canton',
      render: 'list',
      promptDe: 'In welchem Kanton wohnen Sie?',
      options: cantonOptions,
      leadField: 'customFields.canton',
      note: 'Unsupported canton → disqualifier "unsupported_canton".',
    },
    {
      id: 'krankenkasse',
      render: 'text',
      promptDe: 'Bei welcher Krankenkasse ist die gepflegte Person versichert?',
      // REQUIRED (operator Osman, 2026-06-06): Spitex Frei bills the
      // Angehoerigenpflege placement via the patient's insurer, so the lead is
      // not forwardable without it. Maps to the same lead field the supply
      // conversation gate enforces for `mayComplete`.
      leadField: 'customFields.krankenkasse',
      note: 'Required for forwardability. No disqualifier branch — a missing answer keeps the lead open (the agent re-asks), it never rejects the lead.',
    },
    {
      id: 'medizinische_ausbildung',
      render: 'buttons',
      promptDe: 'Haben Sie eine medizinische oder pflegerische Ausbildung?',
      options: [
        { value: 'ja_abgeschlossen', labelDe: 'Ja, abgeschlossen' },
        { value: 'in_ausbildung', labelDe: 'In Ausbildung' },
        { value: 'nein', labelDe: 'Keine' },
      ],
      // CAPTURED, NEVER a disqualifier (operator Osman, 2026-06-06): Spitex Frei
      // employs untrained caregivers and covers the Pflegehelferkurs post-hire.
      // No matching entry in `disqualifiers` by design — any answer (incl.
      // "nein") proceeds to the reveal.
      leadField: 'customFields.medizinischeAusbildung',
      note: 'Optional capture. "nein" must NOT trigger a disqualifier; all answers continue to reveal.',
    },
    {
      id: 'reveal',
      render: 'text',
      promptDe:
        '(synthetic) Render revealTemplateDe with estimateCaregiverWage output.',
      leadField: null,
      note: 'Call estimateCaregiverWage({ hoursPerDay, tasks, canton }). If eligible → show revealTemplateDe; else branch to the matching disqualifier by result.reason.',
    },
    {
      id: 'handoff',
      render: 'text',
      promptDe:
        '(synthetic) Render handoffTemplateDe; capture consent + name/email via submit_lead_data.',
      leadField: null,
      note: 'Goal is capture + human follow-up, NOT in-chat close. Consent maps to consentGiven; then a Spitex Frei coordinator follows up.',
    },
  ],

  disqualifiers: [
    {
      id: 'no_caregiving',
      when: 'situation == "no" (the person does not and will not provide care)',
      messageDe:
        'Danke für Ihr Interesse. Dieses Angebot richtet sich an Personen, ' +
        'die bereits einen Angehörigen pflegen. Melden Sie sich gerne, wenn ' +
        'sich das ändert.',
    },
    {
      id: 'no_billable_tasks',
      when: 'no selected task is in the billable Grundpflege list (I3)',
      messageDe:
        'Vielen Dank. Für einen möglichen Lohnanspruch braucht es ' +
        'Grundpflege-Tätigkeiten (z.B. Waschen, Ankleiden, Mobilisation). ' +
        'Eine Koordinatorin von Spitex Frei kann Ihre Situation gerne prüfen.',
      reason: 'no_billable_tasks',
    },
    {
      id: 'unsupported_canton',
      when: 'canton not in PLACEHOLDER_SUPPORTED_CANTONS (Spitex Frei footprint, I2/I3)',
      messageDe:
        'Vielen Dank. Leider ist Spitex Frei in Ihrem Kanton noch nicht ' +
        'tätig. Für andere Regionen: spitex.ch.',
      reason: 'unsupported_canton',
    },
  ],

  revealTemplateDe:
    'Auf Basis Ihrer Angaben ({{hours}} Std./Tag) könnte Ihr Lohnanspruch ' +
    'bei rund CHF {{monthly}} pro Monat (ca. CHF {{annual}} pro Jahr) liegen. ' +
    '{{disclaimer}}',

  handoffTemplateDe:
    'Möchten Sie, dass eine Koordinatorin von Spitex Frei sich bei Ihnen ' +
    'meldet, um Ihren Anspruch konkret zu prüfen? Dürfen wir Ihre Angaben ' +
    'dafür an Spitex Frei weiterleiten?',
};
